import React from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useAddCustomerAgreement } from 'hooks/mutations/useAddCustomerAgreement'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import type { BaseErrorResponse } from 'types/beneficiaries'
import type { CreateCustomerAgreement } from 'types/customers'
import { ConfirmDetailsLayout } from './ConfirmDetailsLayout'

export interface AddCustomerAgreementConfirmDetailsProps {
  formData: CreateCustomerAgreement
  displayData: Partial<Record<keyof CreateCustomerAgreement, string>>
  onSuccess: () => void
  onCancel: () => void
}

export const AddCustomerAgreementConfirmDetails = ({
  formData,
  displayData,
  onSuccess,
  onCancel,
}: AddCustomerAgreementConfirmDetailsProps) => {
  const { t } = useTranslation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: submitAgreement, isPending: isSubmitting } = useAddCustomerAgreement()
  const customerModel = useAtomValue(customerModelAtom)

  const onConfirm = () => {
    resetRequestError()
    submitAgreement(
      { ...formData, model: customerModel },
      {
        onSuccess,
        onError: (error: BaseErrorResponse) => {
          setRequestError({
            show: true,
            errorCode: error?.status,
            handledMessage: error?.data?.message,
          })
        },
      }
    )
  }

  const confirmItems = [
    { id: 'customerName', title: t('customers.businessName'), body: formData.customerName },
    { id: 'reference', title: t('customers.externalReference'), body: formData.reference },
    { id: 'useCase', title: t('customers.useCase'), body: displayData.useCase || formData.useCase },
    {
      id: 'countryCode',
      title: t('customers.countryCode'),
      body: displayData.countryCode || formData.countryCode,
    },
  ]

  return (
    <ConfirmDetailsLayout
      confirmItems={confirmItems}
      requestError={requestError}
      isSubmitting={isSubmitting}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
